/*!
 * Accessibility toolbar widget.
 * Adds a launcher button (bottom-left, every page) that opens a panel
 * with real, functional accessibility controls: text size, high
 * contrast, underlined links, a more readable font, reduced motion,
 * and a larger cursor. Preferences persist across pages/visits via
 * localStorage and are fully operable by keyboard.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'a11y_prefs_v1';

  var TEXT_SIZE_CLASSES = ['a11y-fs-1', 'a11y-fs-2', 'a11y-fs-3', 'a11y-fs-4'];

  var defaults = {
    fontStep: 0, // 0 = normal, 1-4 = a11y-fs-1..4
    contrast: false,
    underline: false,
    readable: false,
    stopMotion: false,
    bigCursor: false
  };

  function loadPrefs() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return Object.assign({}, defaults);
      var parsed = JSON.parse(raw);
      return Object.assign({}, defaults, parsed);
    } catch (e) {
      return Object.assign({}, defaults);
    }
  }

  function savePrefs(prefs) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch (e) {
      /* ignore (private browsing / storage disabled) */
    }
  }

  function applyPrefs(prefs) {
    var html = document.documentElement;

    TEXT_SIZE_CLASSES.forEach(function (c) {
      html.classList.remove(c);
    });
    if (prefs.fontStep > 0) {
      html.classList.add(TEXT_SIZE_CLASSES[prefs.fontStep - 1]);
    }

    html.classList.toggle('a11y-contrast', !!prefs.contrast);
    html.classList.toggle('a11y-underline', !!prefs.underline);
    html.classList.toggle('a11y-readable', !!prefs.readable);
    html.classList.toggle('a11y-stop-motion', !!prefs.stopMotion);
    html.classList.toggle('a11y-big-cursor', !!prefs.bigCursor);
  }

  // Apply saved prefs immediately (before building the UI) so every
  // page reflects the visitor's choices right away.
  var prefs = loadPrefs();
  applyPrefs(prefs);

  var STRINGS = {
    launcherLabel: 'תפריט נגישות',
    panelTitle: 'התאמות נגישות',
    close: 'סגור',
    textSize: 'גודל טקסט',
    decrease: 'הקטן',
    reset: 'איפוס',
    increase: 'הגדל',
    contrast: 'ניגודיות גבוהה',
    underline: 'קו תחתון לקישורים',
    readable: 'גופן קריא',
    stopMotion: 'עצירת אנימציות',
    bigCursor: 'סמן עכבר גדול',
    resetAll: 'איפוס כל ההגדרות',
    on: 'פעיל',
    off: 'כבוי'
  };

  function buildUI() {
    var toggle = document.createElement('button');
    toggle.id = 'a11y-toggle';
    toggle.type = 'button';
    toggle.setAttribute('aria-haspopup', 'dialog');
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-controls', 'a11y-panel');
    toggle.setAttribute('aria-label', STRINGS.launcherLabel);
    toggle.title = STRINGS.launcherLabel;
    toggle.innerHTML =
      '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
      '<circle cx="12" cy="4" r="2"></circle>' +
      '<path d="M12 7c-3.3 0-8 1-8 3v2h6v9h4v-9h6v-2c0-2-4.7-3-8-3z"></path>' +
      '</svg>';

    var panel = document.createElement('div');
    panel.id = 'a11y-panel';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'false');
    panel.setAttribute('aria-labelledby', 'a11y-panel-title');
    panel.hidden = true;

    panel.innerHTML =
      '<button type="button" class="a11y-close" aria-label="' + STRINGS.close + '">&times;</button>' +
      '<h2 id="a11y-panel-title">' + STRINGS.panelTitle + '</h2>' +

      '<div class="a11y-group">' +
        '<span class="a11y-group-label">' + STRINGS.textSize + '</span>' +
        '<div class="a11y-row">' +
          '<button type="button" class="a11y-btn" data-action="font-dec">' + STRINGS.decrease + ' A-</button>' +
          '<button type="button" class="a11y-btn" data-action="font-reset">' + STRINGS.reset + '</button>' +
          '<button type="button" class="a11y-btn" data-action="font-inc">' + STRINGS.increase + ' A+</button>' +
        '</div>' +
      '</div>' +

      '<div class="a11y-group">' +
        '<div class="a11y-row">' +
          '<button type="button" class="a11y-btn" data-toggle="contrast" aria-pressed="false">' + STRINGS.contrast + '</button>' +
        '</div>' +
      '</div>' +

      '<div class="a11y-group">' +
        '<div class="a11y-row">' +
          '<button type="button" class="a11y-btn" data-toggle="underline" aria-pressed="false">' + STRINGS.underline + '</button>' +
        '</div>' +
      '</div>' +

      '<div class="a11y-group">' +
        '<div class="a11y-row">' +
          '<button type="button" class="a11y-btn" data-toggle="readable" aria-pressed="false">' + STRINGS.readable + '</button>' +
        '</div>' +
      '</div>' +

      '<div class="a11y-group">' +
        '<div class="a11y-row">' +
          '<button type="button" class="a11y-btn" data-toggle="stopMotion" aria-pressed="false">' + STRINGS.stopMotion + '</button>' +
        '</div>' +
      '</div>' +

      '<div class="a11y-group">' +
        '<div class="a11y-row">' +
          '<button type="button" class="a11y-btn" data-toggle="bigCursor" aria-pressed="false">' + STRINGS.bigCursor + '</button>' +
        '</div>' +
      '</div>' +

      '<button type="button" class="a11y-reset" data-action="reset-all">' + STRINGS.resetAll + '</button>';

    document.body.appendChild(panel);
    document.body.appendChild(toggle);

    return { toggle: toggle, panel: panel };
  }

  function refreshUI(ui) {
    TEXT_SIZE_CLASSES.forEach(function () {}); // no-op, kept for clarity

    var toggles = ui.panel.querySelectorAll('[data-toggle]');
    toggles.forEach(function (btn) {
      var key = btn.getAttribute('data-toggle');
      var isOn = !!prefs[key];
      btn.setAttribute('aria-pressed', isOn ? 'true' : 'false');
    });
  }

  function trapFocus(panel, event) {
    var focusable = panel.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (event.key === 'Tab') {
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  function init() {
    var ui = buildUI();
    refreshUI(ui);

    function openPanel() {
      ui.panel.hidden = false;
      ui.toggle.setAttribute('aria-expanded', 'true');
      var firstFocusable = ui.panel.querySelector('button, [href], input, select, textarea');
      if (firstFocusable) firstFocusable.focus();
    }

    function closePanel(returnFocus) {
      ui.panel.hidden = true;
      ui.toggle.setAttribute('aria-expanded', 'false');
      if (returnFocus !== false) ui.toggle.focus();
    }

    ui.toggle.addEventListener('click', function () {
      if (ui.panel.hidden) {
        openPanel();
      } else {
        closePanel();
      }
    });

    ui.panel.querySelector('.a11y-close').addEventListener('click', function () {
      closePanel();
    });

    ui.panel.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        closePanel();
      } else {
        trapFocus(ui.panel, event);
      }
    });

    document.addEventListener('click', function (event) {
      if (
        !ui.panel.hidden &&
        !ui.panel.contains(event.target) &&
        event.target !== ui.toggle &&
        !ui.toggle.contains(event.target)
      ) {
        closePanel(false);
      }
    });

    ui.panel.addEventListener('click', function (event) {
      var target = event.target;
      var action = target.getAttribute && target.getAttribute('data-action');
      var toggleKey = target.getAttribute && target.getAttribute('data-toggle');

      if (action === 'font-inc') {
        prefs.fontStep = Math.min(4, prefs.fontStep + 1);
      } else if (action === 'font-dec') {
        prefs.fontStep = Math.max(0, prefs.fontStep - 1);
      } else if (action === 'font-reset') {
        prefs.fontStep = 0;
      } else if (action === 'reset-all') {
        prefs = Object.assign({}, defaults);
      } else if (toggleKey) {
        prefs[toggleKey] = !prefs[toggleKey];
      } else {
        return;
      }

      applyPrefs(prefs);
      savePrefs(prefs);
      refreshUI(ui);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
